/**
 * IssuerSubmissionPage - 发行人信息提交页面
 *
 * 功能：
 * 1. 项目基本信息表单（复用现有数据）
 * 2. 环境可行性报告上传
 * 3. 财务数据文档上传
 * 4. 其他补充材料上传
 * 5. 提交状态跟踪
 * 6. 已提交信息展示
 *
 * 权限控制：
 * - 发行人：提交、编辑未审核信息
 * - 运营商/管理员：查看、协助提交
 * - 其他角色：仅查看
 */

// @ts-nocheck - TODO: Fix type definitions
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  Button,
  Space,
  Alert,
  Descriptions,
  Modal,
  message,
  Spin,
  Timeline,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Tag,
  Progress,
  List,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  InboxOutlined,
  EyeOutlined,
  DeleteOutlined,
  SendOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import {
  useGetSPOQuery,
  useSubmitIssuerInfoMutation,
  useGetIssuerStatusQuery,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

interface IssuerSubmissionPageProps {
  projectId?: string;
}

const IssuerSubmissionPage: React.FC<IssuerSubmissionPageProps> = ({
  projectId: propProjectId,
}) => {
  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
  });

  // ==================== State管理 ====================
  const [form] = Form.useForm();
  const [environmentalReports, setEnvironmentalReports] = useState<
    UploadFile[]
  >([]);
  const [financialDocuments, setFinancialDocuments] = useState<UploadFile[]>(
    []
  );
  const [supplementaryDocs, setSupplementaryDocs] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);

  // ==================== API Hooks ====================
  const {
    data: spoData,
    isLoading: spoLoading,
    error: spoError,
  } = useGetSPOQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const {
    data: issuerStatusData,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useGetIssuerStatusQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const [submitIssuerInfo, { isLoading: submitting }] =
    useSubmitIssuerInfoMutation();

  // ==================== 权限控制 ====================
  const userRole = 'issuer'; // TODO: 从Redux Store获取实际用户角色
  const canEdit =
    (userRole === 'issuer' ||
      userRole === 'operator' ||
      userRole === 'admin') &&
    issuerStatusData?.data?.evaluation?.issuerSubmissionStatus !== 'COMPLETED';

  // ==================== 表单初始化 ====================
  useEffect(() => {
    const evaluation = spoData?.data?.evaluation;
    if (evaluation?.projectInfo) {
      const projectInfo = evaluation.projectInfo;
      form.setFieldsValue({
        projectName: projectInfo.projectName,
        projectType: projectInfo.projectType,
        projectScale: projectInfo.projectScale,
        estimatedAmount: projectInfo.estimatedAmount,
        currency: projectInfo.currency || 'USD',
        projectDescription: projectInfo.projectDescription,
        greenImpact: projectInfo.greenImpact,
        expectedClosingDate: projectInfo.expectedClosingDate
          ? dayjs(projectInfo.expectedClosingDate)
          : undefined,
      });
    }
  }, [spoData, form]);

  // ==================== 文件上传处理 ====================
  const createUploadProps = (
    fileList: UploadFile[],
    setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>,
    fileType: string
  ): UploadProps => ({
    name: 'file',
    multiple: true,
    fileList,
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png',
    beforeUpload: file => {
      const isValidType =
        file.type === 'application/pdf' ||
        file.type.startsWith('application/vnd') ||
        file.type.startsWith('image/');
      if (!isValidType) {
        message.error('只能上传PDF、Word、Excel或图片文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('文件大小不能超过20MB！');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: info => {
      setFileList(info.fileList);
    },
    onRemove: file => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    },
    onPreview: file => {
      setPreviewFile(file);
      setPreviewVisible(true);
    },
  });

  const environmentalUploadProps = createUploadProps(
    environmentalReports,
    setEnvironmentalReports,
    'environmental'
  );

  const financialUploadProps = createUploadProps(
    financialDocuments,
    setFinancialDocuments,
    'financial'
  );

  const supplementaryUploadProps = createUploadProps(
    supplementaryDocs,
    setSupplementaryDocs,
    'supplementary'
  );

  // ==================== 提交发行人信息 ====================
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (
        environmentalReports.length === 0 ||
        financialDocuments.length === 0
      ) {
        message.error('请至少上传环境可行性报告和财务数据文档！');
        return;
      }

      // 构建项目信息数据
      const projectInfo = {
        projectName: values.projectName,
        projectType: values.projectType,
        projectScale: values.projectScale,
        estimatedAmount: values.estimatedAmount,
        currency: values.currency,
        projectDescription: values.projectDescription,
        greenImpact: values.greenImpact,
        expectedClosingDate: values.expectedClosingDate.toISOString(),
      };

      // 构建文档列表（实际应上传到服务器获取URL）
      const documents = [
        ...environmentalReports.map(file => ({
          type: 'environmental_report',
          filename: file.name,
          url: 'https://example.com/environmental/' + file.uid, // TODO: 实际文件URL
        })),
        ...financialDocuments.map(file => ({
          type: 'financial_data',
          filename: file.name,
          url: 'https://example.com/financial/' + file.uid, // TODO: 实际文件URL
        })),
        ...supplementaryDocs.map(file => ({
          type: 'supplementary',
          filename: file.name,
          url: 'https://example.com/supplementary/' + file.uid, // TODO: 实际文件URL
        })),
      ];

      const result = await submitIssuerInfo({
        spoId: resolvedSpoId!,
        data: {
          projectInfo,
          documents,
        },
      }).unwrap();

      if (result.success) {
        message.success('发行人信息提交成功！');
        refetchStatus();
        // 清空文件列表
        setEnvironmentalReports([]);
        setFinancialDocuments([]);
        setSupplementaryDocs([]);
      } else {
        message.error(result.error?.message || '提交失败');
      }
    } catch (error) {
      console.error('Submit issuer info error:', error);
      message.error('提交失败，请检查表单');
    }
  };

  // ==================== 渲染提交状态 ====================
  const renderSubmissionStatus = () => {
    const evaluation = issuerStatusData?.data?.evaluation;

    if (!evaluation || !evaluation.issuerSubmissionStatus) {
      return (
        <Alert
          message="未提交信息"
          description="请填写项目信息并上传相关文档后提交"
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
        />
      );
    }

    return (
      <Card title="提交状态" style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="提交状态" span={2}>
            <UnifiedStatusTag
              type="evaluationStatus"
              status={evaluation.issuerSubmissionStatus}
            />
          </Descriptions.Item>
          <Descriptions.Item label="提交时间">
            {evaluation.submittedAt
              ? dayjs(evaluation.submittedAt).format('YYYY-MM-DD HH:mm')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="提交人">
            {evaluation.submittedBy || '-'}
          </Descriptions.Item>
          {evaluation.projectInfo && (
            <>
              <Descriptions.Item label="项目名称" span={2}>
                {evaluation.projectInfo.projectName}
              </Descriptions.Item>
              <Descriptions.Item label="项目类型">
                {evaluation.projectInfo.projectType}
              </Descriptions.Item>
              <Descriptions.Item label="项目规模">
                {evaluation.projectInfo.projectScale}
              </Descriptions.Item>
              <Descriptions.Item label="预计发行金额">
                {evaluation.projectInfo.estimatedAmount}{' '}
                {evaluation.projectInfo.currency}
              </Descriptions.Item>
              <Descriptions.Item label="预期交割日期">
                {evaluation.projectInfo.expectedClosingDate
                  ? dayjs(evaluation.projectInfo.expectedClosingDate).format(
                      'YYYY-MM-DD'
                    )
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="项目描述" span={2}>
                {evaluation.projectInfo.projectDescription}
              </Descriptions.Item>
              <Descriptions.Item label="绿色影响" span={2}>
                {evaluation.projectInfo.greenImpact}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>

        {/* 已上传文档列表 */}
        {evaluation.documents && evaluation.documents.length > 0 && (
          <>
            <Divider>已上传文档</Divider>
            <List
              size="small"
              dataSource={evaluation.documents}
              renderItem={doc => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => window.open(doc.url, '_blank')}
                    >
                      查看
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      doc.type === 'environmental_report' ? (
                        <FileTextOutlined
                          style={{ fontSize: 24, color: '#52c41a' }}
                        />
                      ) : doc.type === 'financial_data' ? (
                        <FilePdfOutlined
                          style={{ fontSize: 24, color: '#1890ff' }}
                        />
                      ) : (
                        <FileTextOutlined
                          style={{ fontSize: 24, color: '#8c8c8c' }}
                        />
                      )
                    }
                    title={doc.filename}
                    description={
                      doc.type === 'environmental_report'
                        ? '环境可行性报告'
                        : doc.type === 'financial_data'
                          ? '财务数据文档'
                          : '补充材料'
                    }
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Card>
    );
  };

  // ==================== 渲染信息表单 ====================
  const renderInfoForm = () => {
    if (!canEdit) {
      return (
        <Alert
          message="无编辑权限"
          description="您当前没有权限编辑发行人信息，或信息已提交"
          type="info"
          showIcon
        />
      );
    }

    return (
      <Card title="项目信息填写" style={{ marginBottom: 24 }}>
        <Alert
          message="填写提示"
          description="请填写完整的项目信息，并上传环境可行性报告和财务数据文档"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="项目名称"
                name="projectName"
                rules={[{ required: true, message: '请输入项目名称' }]}
              >
                <Input placeholder="请输入项目名称" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="项目类型"
                name="projectType"
                rules={[{ required: true, message: '请选择项目类型' }]}
              >
                <Select placeholder="请选择项目类型">
                  <Option value="renewable_energy">可再生能源</Option>
                  <Option value="energy_efficiency">能源效率</Option>
                  <Option value="pollution_prevention">污染防治</Option>
                  <Option value="sustainable_water">可持续水资源管理</Option>
                  <Option value="clean_transportation">清洁交通</Option>
                  <Option value="green_building">绿色建筑</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="项目规模"
                name="projectScale"
                rules={[{ required: true, message: '请选择项目规模' }]}
              >
                <Select placeholder="请选择项目规模">
                  <Option value="small">小型（&lt; 1亿）</Option>
                  <Option value="medium">中型（1-5亿）</Option>
                  <Option value="large">大型（&gt; 5亿）</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="预计发行金额"
                name="estimatedAmount"
                rules={[
                  { required: true, message: '请输入预计发行金额' },
                  { type: 'number', min: 0, message: '金额必须大于0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入金额"
                  precision={2}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="币种"
                name="currency"
                initialValue="USD"
                rules={[{ required: true, message: '请选择币种' }]}
              >
                <Select placeholder="请选择币种">
                  <Option value="USD">美元 (USD)</Option>
                  <Option value="CNY">人民币 (CNY)</Option>
                  <Option value="EUR">欧元 (EUR)</Option>
                  <Option value="GBP">英镑 (GBP)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="预期交割日期"
            name="expectedClosingDate"
            rules={[{ required: true, message: '请选择预期交割日期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择日期"
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item
            label="项目描述"
            name="projectDescription"
            rules={[
              { required: true, message: '请输入项目描述' },
              { min: 50, message: '项目描述至少50个字符' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请详细描述项目的背景、目标、实施计划等"
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="绿色环境影响"
            name="greenImpact"
            rules={[
              { required: true, message: '请输入绿色环境影响' },
              { min: 30, message: '绿色环境影响至少30个字符' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请描述项目对环境的正面影响，如减排量、节能效果等"
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Divider>文档上传</Divider>

          <Form.Item
            label="环境可行性报告（必填）"
            required
            extra="请上传完整的环境可行性报告，支持PDF、Word格式"
          >
            <Dragger {...environmentalUploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持PDF、Word、Excel、图片格式，单个文件不超过20MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item
            label="财务数据文档（必填）"
            required
            extra="请上传最近3年的财务报表、审计报告等"
          >
            <Dragger {...financialUploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持PDF、Excel格式，单个文件不超过20MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item
            label="其他补充材料（可选）"
            extra="如有其他支持性文档，可在此上传"
          >
            <Dragger {...supplementaryUploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持多种文档格式，单个文件不超过20MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<SendOutlined />}
                loading={submitting}
                onClick={handleSubmit}
              >
                提交发行人信息
              </Button>
              <Button
                size="large"
                onClick={() => {
                  form.resetFields();
                  setEnvironmentalReports([]);
                  setFinancialDocuments([]);
                  setSupplementaryDocs([]);
                }}
              >
                重置表单
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    );
  };

  // ==================== Loading/Error处理 ====================
  if (isResolving || spoLoading || statusLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载发行人信息中...</div>
      </div>
    );
  }

  if (spoError || isResolveError || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="无法加载SPO流程信息，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  // ==================== 主渲染 ====================
  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>发行人信息提交</Title>
      <Paragraph type="secondary">
        请填写完整的项目信息并上传相关文档，以便S&P进行评估
      </Paragraph>

      <Divider />

      {/* 提交状态 */}
      {renderSubmissionStatus()}

      {/* 信息表单 */}
      {renderInfoForm()}

      {/* 文件预览Modal */}
      <Modal
        open={previewVisible}
        title="文件预览"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
      >
        {previewFile && (
          <div>
            <p>
              <strong>文件名：</strong>
              {previewFile.name}
            </p>
            <p>
              <strong>文件类型：</strong>
              {previewFile.type}
            </p>
            <p>
              <strong>文件大小：</strong>
              {((previewFile.size || 0) / 1024 / 1024).toFixed(2)} MB
            </p>
            <Alert
              message="文件预览"
              description="实际文件预览需要服务器支持，此处仅显示文件信息"
              type="info"
              showIcon
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default IssuerSubmissionPage;
